import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { AppLayout } from '../layouts/AppLayout';
import { LoginPage } from '../pages/auth/LoginPage';
import { DashboardPage } from '../pages/dashboard/DashboardPage';
import { PosPage } from '../pages/pos/PosPage';
import { SalesListPage } from '../pages/sales/SalesListPage';
import { EstimatesPage } from '../pages/sales/EstimatesPage';
import { StatementsPage } from '../pages/reports/StatementsPage';
import { LocationsPage } from '../pages/inventory/LocationsPage';
import { ChitsPage } from '../pages/chit/ChitsPage';
import { ChitSchemesPage } from '../pages/chit/ChitSchemesPage';
import { ChitReceiptPage } from '../pages/chit/ChitReceiptPage';
import { MetalsPage } from '../pages/settings/MetalsPage';
import { EstimatePrintPage } from '../pages/sales/EstimatePrintPage';
import { AccountsOverviewPage } from '../pages/accounts/AccountsOverviewPage';
import { MastersPage } from '../pages/settings/MastersPage';
import { ChargesPage } from '../pages/settings/ChargesPage';
import { ControlsPage } from '../pages/settings/ControlsPage';
import { ExchangePage } from '../pages/sales/ExchangePage';
import { InvoicePrintPage } from '../pages/sales/InvoicePrintPage';
import { CreditNotePrintPage } from '../pages/sales/CreditNotePrintPage';
import { AdvancesPage } from '../pages/accounts/AdvancesPage';
import { AdvancePrintPage } from '../pages/accounts/AdvancePrintPage';
import { OldGoldPrintPage } from '../pages/purchasing/OldGoldPrintPage';
import { PurchaseEntryPage } from '../pages/purchasing/PurchaseEntryPage';
import { OldGoldPage } from '../pages/purchasing/OldGoldPage';
import { SuppliersPage } from '../pages/purchasing/SuppliersPage';
import { PurchaseReturnsPage } from '../pages/purchasing/PurchaseReturnsPage';
import { CustomersPage } from '../pages/parties/CustomersPage';
import { ProductsPage } from '../pages/catalog/ProductsPage';
import { RatesPage } from '../pages/catalog/RatesPage';
import { ItemsPage } from '../pages/inventory/ItemsPage';
import { TransfersPage } from '../pages/inventory/TransfersPage';
import { AdjustmentsPage } from '../pages/inventory/AdjustmentsPage';
import { AuditPage } from '../pages/inventory/AuditPage';
import { JobWorkPage } from '../pages/production/JobWorkPage';
import { MeltingPage } from '../pages/production/MeltingPage';
import { MeltingPrintPage } from '../pages/production/MeltingPrintPage';
import { MeltersPage } from '../pages/production/MeltersPage';
import { BarTagsPrintPage } from '../pages/production/BarTagsPrintPage';
import { MeltLotsPage } from '../pages/production/MeltLotsPage';
import { MetalPositionPage } from '../pages/production/MetalPositionPage';
import { ReportsPage } from '../pages/reports/ReportsPage';
import { ApprovalsPage } from '../pages/workflow/ApprovalsPage';
import { UsersPage } from '../pages/settings/UsersPage';
import { SettingsPage } from '../pages/settings/SettingsPage';
import { AccountPage } from '../pages/account/AccountPage';
import { ExpensesPage } from '../pages/accounts/ExpensesPage';
import { IncomePage } from '../pages/accounts/IncomePage';
import { PaymentsPage } from '../pages/accounts/PaymentsPage';
import { CashPage } from '../pages/accounts/CashPage';
import { LedgerPage } from '../pages/accounts/LedgerPage';
import { JournalsPage } from '../pages/accounts/JournalsPage';
import { PeriodsPage } from '../pages/accounts/PeriodsPage';
import { useAuth } from '../store/auth';

/**
 * Every screen in one place. Unauthenticated, there is exactly one route —
 * there is nothing else worth showing somebody who has not signed in.
 */
export function AppRoutes() {
  const signedIn = useAuth((s) => s.accessToken !== null);
  if (!signedIn) return <LoginPage />;

  return (
    // A screen that throws shows what went wrong rather than emptying the window.
    <ErrorBoundary>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="pos" element={<PosPage />} />
          <Route path="sales" element={<SalesListPage />} />
          <Route path="estimates" element={<EstimatesPage />} />
          <Route path="statements" element={<StatementsPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="chits" element={<ChitsPage />} />
          <Route path="chit-schemes" element={<ChitSchemesPage />} />
          <Route path="metals" element={<MetalsPage />} />
          <Route path="accounts" element={<AccountsOverviewPage />} />
          <Route path="lists" element={<MastersPage />} />
          <Route path="charges" element={<ChargesPage />} />
          <Route path="controls" element={<ControlsPage />} />
          <Route path="estimates/:id/print" element={<EstimatePrintPage />} />
          <Route path="chits/:id/receipt/:no" element={<ChitReceiptPage />} />
          <Route path="exchange" element={<ExchangePage />} />
          <Route path="sales/:id/print" element={<InvoicePrintPage />} />
          <Route path="sales-returns/:id/print" element={<CreditNotePrintPage />} />
          <Route path="advances/:id/print" element={<AdvancePrintPage />} />
          <Route path="purchases/:id/print" element={<OldGoldPrintPage />} />
          <Route path="purchases" element={<PurchaseEntryPage />} />
          <Route path="old-gold" element={<OldGoldPage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="purchase-returns" element={<PurchaseReturnsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="items" element={<ItemsPage />} />
          <Route path="transfers" element={<TransfersPage />} />
          <Route path="adjustments" element={<AdjustmentsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="job-work" element={<JobWorkPage />} />
          <Route path="melting" element={<MeltingPage />} />
          <Route path="melting/:id/print" element={<MeltingPrintPage />} />
          <Route path="melting/:id/tags" element={<BarTagsPrintPage />} />
          <Route path="melting/melters" element={<MeltersPage />} />
          <Route path="melting/lots" element={<MeltLotsPage />} />
          <Route path="melting/position" element={<MetalPositionPage />} />
          <Route path="rates" element={<RatesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="income" element={<IncomePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="advances" element={<AdvancesPage />} />
          <Route path="cash" element={<CashPage />} />
          <Route path="ledgers" element={<LedgerPage />} />
          <Route path="journals" element={<JournalsPage />} />
          <Route path="periods" element={<PeriodsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
